import {
  faBlockQuote,
  faBold,
  faChain,
  faChainBroken,
  faChevronDown,
  faCopy,
  faExternalLinkSquareAlt,
  faHeading,
  faHorizontalRule,
  faItalic,
  faListOl,
  faListUl,
  faParagraph,
  faPhotoVideo,
  faRedo,
  faRemoveFormat,
  faStrikethrough,
  faTrash,
  faUndo,
  faUnlink,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getMarkRange, posToDOMRect, useCurrentEditor, useEditorState } from "@tiptap/react";
import classNames from "classnames";
import { PropsWithChildren, useEffect, useRef } from "react";
import { FocusScope, useOverlay } from "react-aria";
import { Button as AriaButton, ListBox, ListBoxItem, Popover, Select, SelectValue } from "react-aria-components";
import { useToasts } from "react-toast-notifications";
import { Button, Divider, Form, Icon, Input, Popup } from "semantic-ui-react";
import { create } from "zustand";
import { useScrollFadeHorizontal } from "../../../../../features/global/hooks/useScrollFadeHorizontal";
import { useUserAgent } from "../../../../../hooks/useUserAgent";
import { RichTextEditorFeatures, RichTextEditorFormat, RichTextEditorMediaProps } from "../ControlledRichTextEditor";
import { useControlledRichTextEditorMenuBarStore } from "../providers/ControlledRichTextEditorMenuBarProvider";
import { checkFeature } from "../util/checkFeature";
import { ControlledRichTextEditorCustomBubbleMenu } from "./ControlledRichTextEditorCustomBubbleMenu";
import { ControlledRichTextEditorCustomFloatingMenu } from "./ControlledRichTextEditorCustomFloatingMenu";
import { ControlledRichTextEditorInsertMediaModal } from "./ControlledRichTextEditorInsertMediaModal";

export function ControlledRichTextEditorMenuBar({
  features = {},
  format,
  hasWhiteMenuBar,
  mediaFileUsage,
  addMediaFile,
}: {
  format: RichTextEditorFormat;
  features?: RichTextEditorFeatures;
  hasWhiteMenuBar?: boolean;
} & RichTextEditorMediaProps) {
  const { editor } = useCurrentEditor();
  const { addToast } = useToasts();
  const {
    isLinkMenuVisible,
    setIsLinkMenuVisible,
    mediaModalData: imageModalData,
    setMediaModalData: setImageModalData,
    linkDisplayText,
    linkUrl,
    setLinkDisplayText,
    setLinkUrl,
  } = useControlledRichTextEditorMenuBarStore();
  const linkMenuRef = useRef<HTMLDivElement>(null);
  const linkMenuInputRef = useRef<HTMLInputElement>(null);

  const scrollRef = useRef(null);
  const { containerClassNames } = useScrollFadeHorizontal(scrollRef);

  if (!editor) {
    return null;
  }

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      const falseIfError = (callback: () => boolean) => {
        try {
          return callback();
        } catch {
          return false;
        }
      };
      return {
        isBold: falseIfError(() => ctx.editor.isActive("bold") ?? false),
        canBold: falseIfError(() => ctx.editor.can().chain().toggleBold().run() ?? false),
        isItalic: falseIfError(() => ctx.editor.isActive("italic") ?? false),
        canItalic: falseIfError(() => ctx.editor.can().chain().toggleItalic().run() ?? false),
        isStrike: falseIfError(() => ctx.editor.isActive("strike") ?? false),
        canStrike: falseIfError(() => ctx.editor.can().chain().toggleStrike().run() ?? false),
        canClearMarks: falseIfError(() => ctx.editor.can().chain().unsetAllMarks().run() ?? false),
        currentHeadingLevel: falseIfError(() => ctx.editor.getAttributes("heading").level ?? null),
        isBulletList: falseIfError(() => ctx.editor.isActive("bulletList") ?? false),
        isOrderedList: falseIfError(() => ctx.editor.isActive("orderedList") ?? false),
        isBlockquote: falseIfError(() => ctx.editor.isActive("blockquote") ?? false),
        canUndo: falseIfError(() => ctx.editor.can().chain().undo().run() ?? false),
        canRedo: falseIfError(() => ctx.editor.can().chain().redo().run() ?? false),
        isLink: falseIfError(() => ctx.editor.isActive("link") ?? false),
        isImage: falseIfError(() => ctx.editor.isActive("image") ?? false),
        hasSelection: ctx.editor.state.selection?.from !== ctx.editor.state.selection?.to,
        selectedText: ctx.editor.state.doc.textBetween(
          ctx.editor.state.selection?.from,
          ctx.editor.state.selection?.to,
          " ",
        ),
      };
    },
  });

  const showMenuBarOne =
    checkFeature(features.bold).menuButton ||
    checkFeature(features.italic).menuButton ||
    checkFeature(features.strike).menuButton ||
    checkFeature(features.bulletList).menuButton ||
    checkFeature(features.orderedList).menuButton ||
    checkFeature(features.horizontalRule).menuButton ||
    checkFeature(features.blockquote).menuButton ||
    checkFeature(features.clearFormatting).menuButton;
  const showMenuBarTwo = checkFeature(features.link).menuButton || checkFeature(features.media).menuButton;

  const { overlayProps } = useOverlay(
    {
      isOpen: isLinkMenuVisible,
      onClose: () => hideLinkMenu(),
      shouldCloseOnInteractOutside: (e) => {
        if (linkMenuRef.current?.contains(e.firstChild as HTMLElement)) return false;
        return true;
      },
      shouldCloseOnBlur: true,
      isDismissable: true,
      isKeyboardDismissDisabled: false,
    },
    linkMenuRef,
  );

  const showLinkMenu = () => {
    setIsLinkMenuVisible(true);
    setLinkDisplayText(editorState.selectedText || "");
    editor.chain().focus().enableSelectionDecoration().run();
  };

  const hideLinkMenu = () => {
    setLinkUrl("");
    setIsLinkMenuVisible(false);
    setLinkDisplayText("");
    editor.chain().focus().disableSelectionDecoration().run();
  };

  const setLink = () => {
    if (!linkUrl) return;

    // Check if link url is missing protocol, and add it if so
    const missingProtocol = !/^https?:\/\//i.test(linkUrl) && !/^mailto:/i.test(linkUrl) && !/^tel:/i.test(linkUrl);
    const finalLinkUrl = missingProtocol ? `http://${linkUrl}` : linkUrl;

    // If we don't have display text, just insert the link URL as the text
    if (!linkDisplayText) {
      editor
        .chain()
        .focus()
        .insertContent(linkUrl)
        .setTextSelection({
          from: editor.state.selection.from,
          to: editor.state.selection.from + linkUrl.length,
        })
        .setLink({ href: finalLinkUrl })
        .selectTextblockEnd()
        .run();
    }

    // If we have display text, insert that and set it as the link
    if (linkDisplayText) {
      editor
        .chain()
        .focus()
        .insertContent(linkDisplayText)
        .setTextSelection({
          from: editor.state.selection.from,
          to: editor.state.selection.from + linkDisplayText.length,
        })
        .setLink({ href: finalLinkUrl })
        .selectTextblockEnd()
        .run();
    }

    hideLinkMenu();
  };

  useEffect(() => {
    if (!isLinkMenuVisible) return;
    const linkElement = linkMenuRef.current;
    if (linkElement) {
      // It's gross but it works :')
      window.requestAnimationFrame(() => {
        linkElement.focus();
      });
    }
  }, [isLinkMenuVisible]);

  return (
    <>
      {!checkFeature(features.media).anyDisabled && (
        <ControlledRichTextEditorInsertMediaModal
          key={imageModalData?.url}
          data={imageModalData}
          onClose={() => setImageModalData(null)}
          onInsertImage={({ url, altText, isDecorative, uuid }) => {
            editor
              .chain()
              .focus()
              .setImage({ src: url, alt: altText, isDecorative } as any)
              .run();
            if (uuid) addMediaFile?.(uuid);
          }}
          onInsertVideo={({}) => {
            // editor.chain().focus().setVideo(url).run();
            // TODO: Uncomment after adding video support
          }}
          fileUsage={mediaFileUsage!}
          // Markdown doesn't have a place to store the isDecorative flag
          showIsDecorative={format !== "markdown"}
        />
      )}

      {checkFeature(features.media).plugin && (
        <ControlledRichTextEditorCustomBubbleMenu
          editor={editor}
          shouldShow={() => editor.isActive("image")}
          options={{ placement: "bottom", offset: 12 }}
        >
          <div
            className={classNames(
              "tw-relative tw-z-[9999] tw-flex tw-gap-1 tw-rounded tw-bg-white tw-p-[4px] tw-shadow-md tw-ring-1 tw-ring-border",
              {
                "tw-hidden": !!imageModalData,
              },
            )}
          >
            <Button
              onClick={() => {
                // TODO: Add video support
                setImageModalData({
                  type: "image",
                  url: editor.getAttributes("image").src,
                  altText: editor.getAttributes("image").alt || "",
                  isDecorative: editor.getAttributes("image").isDecorative || false,
                });
              }}
              className='tw-m-0 tw-border-none tw-bg-transparent tw-px-[8px] tw-py-[4px] tw-text-text-hint tw-transition-colors tw-duration-100 focus-within:tw-ring-2 focus-within:tw-ring-primary hover:tw-bg-white-dark hover:tw-text-text-secondary'
            >
              Edit
            </Button>
            <div className='tw-h-8 tw-w-[1px] tw-bg-border' />
            <Tooltip content={"Remove Image"} offset={[0, 4]}>
              <Button
                onClick={() => editor.chain().focus().deleteSelection().run()}
                className='tw-m-0 tw-h-8 tw-w-8 tw-border-none tw-bg-transparent tw-p-[4px] tw-text-text-hint tw-transition-colors tw-duration-100 focus-within:tw-ring-2 focus-within:tw-ring-primary hover:tw-bg-white-dark hover:tw-text-text-secondary'
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </Tooltip>
          </div>
        </ControlledRichTextEditorCustomBubbleMenu>
      )}

      {checkFeature(features.link).plugin && (
        <ControlledRichTextEditorCustomBubbleMenu
          editor={editor}
          shouldShow={() => editor.isActive("link")}
          options={{ placement: "bottom-start", offset: 12 }}
          getReferencedVirtualElement={() => {
            const { $from } = editor.state.selection;
            const range = getMarkRange($from, editor.schema.marks.link);

            if (!range) return null;

            const domRect = posToDOMRect(editor.view, range.from, range.to);
            return {
              getBoundingClientRect: () => domRect,
              getClientRects: () => [domRect],
            };
          }}
        >
          <div
            className={classNames(
              "tw-relative tw-z-[9999] tw-flex tw-gap-1 tw-rounded tw-bg-white tw-p-[4px] tw-shadow-md tw-ring-1 tw-ring-border",
              {
                "tw-hidden": isLinkMenuVisible,
              },
            )}
          >
            <Button
              onClick={() => {
                setLinkUrl(editor.getAttributes("link").href);
                editor.commands.extendMarkRange("link");
                showLinkMenu();
                const selectedText = editor.state.doc.textBetween(
                  editor.state.selection?.from,
                  editor.state.selection?.to,
                  " ",
                );
                setLinkDisplayText(selectedText || "");
              }}
              className='tw-m-0 tw-border-none tw-bg-transparent tw-px-[8px] tw-py-[4px] tw-text-text-hint tw-transition-colors tw-duration-100 focus-within:tw-ring-2 focus-within:tw-ring-primary hover:tw-bg-white-dark hover:tw-text-text-secondary'
            >
              Edit Link
            </Button>
            <div className='tw-h-8 tw-w-[1px] tw-bg-border' />
            <Tooltip content={"Open in new tab"} offset={[0, 4]}>
              <Button
                className='tw-m-0 tw-h-8 tw-w-8 tw-border-none tw-bg-transparent tw-p-[4px] tw-text-text-hint tw-transition-colors tw-duration-100 focus-within:tw-ring-2 focus-within:tw-ring-primary hover:tw-bg-white-dark hover:tw-text-text-secondary'
                onClick={() => window.open(editor.getAttributes("link").href, "_blank")}
              >
                <FontAwesomeIcon icon={faExternalLinkSquareAlt} />
              </Button>
            </Tooltip>
            <Tooltip content={"Unlink"} offset={[0, 4]}>
              <Button
                className='tw-m-0 tw-h-8 tw-w-8 tw-border-none tw-bg-transparent tw-p-[4px] tw-text-text-hint tw-transition-colors tw-duration-100 focus-within:tw-ring-2 focus-within:tw-ring-primary hover:tw-bg-white-dark hover:tw-text-text-secondary'
                onClick={() => editor.chain().focus().unsetLink().run()}
              >
                <FontAwesomeIcon icon={faUnlink} />
              </Button>
            </Tooltip>
            <Tooltip content={"Copy URL"} offset={[0, 4]}>
              <Button
                className='tw-m-0 tw-h-8 tw-w-8 tw-border-none tw-bg-transparent tw-p-[4px] tw-text-text-hint tw-transition-colors tw-duration-100 focus-within:tw-ring-2 focus-within:tw-ring-primary hover:tw-bg-white-dark hover:tw-text-text-secondary'
                onClick={() => {
                  navigator.clipboard.writeText(editor.getAttributes("link").href);
                  addToast("Link copied to clipboard", { appearance: "success", autoDismiss: true });
                }}
              >
                <FontAwesomeIcon icon={faCopy} />
              </Button>
            </Tooltip>
          </div>
        </ControlledRichTextEditorCustomBubbleMenu>
      )}

      {checkFeature(features.link).plugin && (
        <ControlledRichTextEditorCustomFloatingMenu
          editor={editor}
          shouldShow={() => true}
          options={{ placement: "bottom-start", offset: 12 }}
          ref={linkMenuRef}
        >
          <div
            className={classNames(
              "tw-relative tw-z-[9999] tw-min-w-80 tw-rounded tw-bg-white tw-px-4 tw-py-4 tw-shadow-md tw-ring-1 tw-ring-border",
              {
                "tw-hidden": !isLinkMenuVisible,
              },
            )}
            {...overlayProps}
          >
            <FocusScope autoFocus contain>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  setLink();
                }}
              >
                <Form.Field>
                  <label htmlFor='link-link'>Link</label>
                  <Input id='link-link' onChange={(_, data) => setLinkUrl(data.value)} value={linkUrl} icon>
                    <input ref={linkMenuInputRef} />
                    {linkUrl && (
                      <Tooltip content={"Clear"}>
                        <Icon
                          aria-hidden='false'
                          className='tw-pointer-events-auto tw-flex tw-items-center tw-justify-center tw-opacity-100'
                        >
                          <button
                            type='button'
                            tabIndex={-1}
                            className='tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-border-none tw-bg-transparent tw-text-text-secondary tw-transition-colors tw-duration-100 hover:tw-text-text'
                            onClick={() => setLinkUrl("")}
                          >
                            <FontAwesomeIcon icon={faXmark} className='tw-h-5 tw-w-5' />
                          </button>
                        </Icon>
                      </Tooltip>
                    )}
                  </Input>
                </Form.Field>
                <Form.Field>
                  <label htmlFor='link-display-text'>Display Text</label>
                  <Input
                    id='link-display-text'
                    onChange={(_, data) => setLinkDisplayText(data.value)}
                    value={linkDisplayText}
                    icon
                  >
                    <input ref={linkMenuInputRef} />
                    {linkDisplayText && (
                      <Tooltip content={"Clear"}>
                        <Icon
                          aria-hidden='false'
                          className='tw-pointer-events-auto tw-flex tw-items-center tw-justify-center tw-opacity-100'
                        >
                          <button
                            type='button'
                            tabIndex={-1}
                            className='tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-border-none tw-bg-transparent tw-text-text-secondary tw-transition-colors tw-duration-100 hover:tw-text-text'
                            onClick={() => setLinkDisplayText("")}
                          >
                            <FontAwesomeIcon icon={faXmark} className='tw-h-5 tw-w-5' />
                          </button>
                        </Icon>
                      </Tooltip>
                    )}
                  </Input>
                </Form.Field>
              </Form>
              <Divider className='-tw-mx-4' />
              <div className='tw-flex tw-justify-end tw-gap-2'>
                <Button className='tw-m-0' secondary size='small' onClick={hideLinkMenu}>
                  Cancel
                </Button>
                <Button type='submit' className='tw-m-0' primary size='small' onClick={setLink} disabled={!linkUrl}>
                  Insert
                </Button>
              </div>
            </FocusScope>
          </div>
        </ControlledRichTextEditorCustomFloatingMenu>
      )}

      <div className={classNames(containerClassNames, "-tw-my-1")}>
        <div className='tw-flex tw-flex-row tw-items-center tw-gap-2 tw-overflow-x-auto tw-py-1' ref={scrollRef}>
          {checkFeature(features.undoRedo).menuButton && (
            <div
              className={classNames("tw-flex tw-rounded tw-p-1", {
                "tw-bg-white-background": !hasWhiteMenuBar,
                "tw-border tw-border-solid tw-border-border tw-bg-white": hasWhiteMenuBar,
              })}
            >
              <MenuBarIcon
                disabled={!editorState.canUndo}
                onClick={() => editor.chain().focus().undo().run()}
                tooltip='Undo'
                hotkey={{ win: "ctrl+z", mac: "cmd+z" }}
              >
                <FontAwesomeIcon icon={faUndo} />
              </MenuBarIcon>
              <MenuBarIcon
                disabled={!editorState.canRedo}
                onClick={() => editor.chain().focus().redo().run()}
                tooltip='Redo'
                hotkey={{ win: "ctrl+shift+z", mac: "cmd+shift+z" }}
              >
                <FontAwesomeIcon icon={faRedo} />
              </MenuBarIcon>
            </div>
          )}
          {checkFeature(features.heading).menuButton && <HeaderDropdown hasWhiteMenuBar={hasWhiteMenuBar} />}
          {showMenuBarOne && (
            <div
              className={classNames("tw-flex tw-rounded tw-p-1", {
                "tw-bg-white-background": !hasWhiteMenuBar,
                "tw-border tw-border-solid tw-border-border tw-bg-white": hasWhiteMenuBar,
              })}
            >
              {checkFeature(features.bold).menuButton && (
                <MenuBarIcon
                  active={editorState.isBold}
                  disabled={!editorState.canBold}
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  tooltip='Bold'
                  hotkey={{ win: "ctrl+b", mac: "cmd+b" }}
                >
                  <FontAwesomeIcon icon={faBold} />
                </MenuBarIcon>
              )}
              {checkFeature(features.italic).menuButton && (
                <MenuBarIcon
                  active={editorState.isItalic}
                  disabled={!editorState.canItalic}
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  tooltip='Italic'
                  hotkey={{ win: "ctrl+i", mac: "cmd+i" }}
                >
                  <FontAwesomeIcon icon={faItalic} />
                </MenuBarIcon>
              )}
              {checkFeature(features.strike).menuButton && (
                <MenuBarIcon
                  active={editorState.isStrike}
                  disabled={!editorState.canStrike}
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  tooltip='Strikethrough'
                  hotkey={{ win: "ctrl+shift+s", mac: "cmd+shift+s" }}
                >
                  <FontAwesomeIcon icon={faStrikethrough} />
                </MenuBarIcon>
              )}
              {checkFeature(features.bulletList).menuButton && (
                <MenuBarIcon
                  active={editorState.isBulletList}
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  tooltip='Bullet List'
                  hotkey={{ win: "ctrl+shift+8", mac: "cmd+shift+8" }}
                >
                  <FontAwesomeIcon icon={faListUl} />
                </MenuBarIcon>
              )}
              {checkFeature(features.orderedList).menuButton && (
                <MenuBarIcon
                  active={editorState.isOrderedList}
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  tooltip='Numbered List'
                  hotkey={{ win: "ctrl+shift+7", mac: "cmd+shift+7" }}
                >
                  <FontAwesomeIcon icon={faListOl} />
                </MenuBarIcon>
              )}
              {checkFeature(features.horizontalRule).menuButton && (
                <MenuBarIcon
                  onClick={() => editor.chain().focus().setHorizontalRule().run()}
                  active={false}
                  tooltip='Horizontal Rule'
                  hotkey={{ win: "---", mac: "---" }}
                >
                  <FontAwesomeIcon icon={faHorizontalRule} />
                </MenuBarIcon>
              )}
              {checkFeature(features.blockquote).menuButton && (
                <MenuBarIcon
                  onClick={() => {
                    if (editorState.isBlockquote) {
                      editor.chain().focus().unsetBlockquote().run();
                    } else {
                      editor.chain().focus().setBlockquote().run();
                    }
                  }}
                  active={editorState.isBlockquote}
                  tooltip='Blockquote'
                  hotkey={{ win: "ctrl+shift+b", mac: "cmd+shift+b" }}
                >
                  <FontAwesomeIcon icon={faBlockQuote} />
                </MenuBarIcon>
              )}
              {checkFeature(features.clearFormatting).menuButton && (
                <MenuBarIcon
                  onClick={() => editor.chain().focus().unsetAllMarks().run()}
                  disabled={!editorState.canClearMarks}
                  tooltip='Clear Formatting'
                  hotkey={{
                    win: "ctrl+\\",
                    mac: "cmd+\\",
                  }}
                >
                  <FontAwesomeIcon icon={faRemoveFormat} />
                </MenuBarIcon>
              )}
            </div>
          )}
          {showMenuBarTwo && (
            <div
              className={classNames("tw-flex tw-rounded tw-p-1", {
                "tw-bg-white-background": !hasWhiteMenuBar,
                "tw-border tw-border-solid tw-border-border tw-bg-white": hasWhiteMenuBar,
              })}
            >
              {checkFeature(features.media).menuButton && (
                <MenuBarIcon
                  active={false}
                  onClick={() =>
                    // TODO: Does this make sense for video?
                    setImageModalData({
                      type: "image",
                      url: "",
                      altText: "",
                      isDecorative: false,
                    })
                  }
                  tooltip='Insert Image'
                >
                  <FontAwesomeIcon icon={faPhotoVideo} />
                </MenuBarIcon>
              )}
              {checkFeature(features.link).menuButton && (
                <MenuBarIcon
                  onClick={() => {
                    if (editorState.isLink) {
                      editor.chain().focus().unsetLink().run();
                      return;
                    }
                    showLinkMenu();
                  }}
                  active={editorState.isLink}
                  tooltip={editorState.isLink ? "Unset" : "Add Link"}
                  disabled={editorState.isImage}
                  hotkey={{
                    win: "ctrl+k",
                    mac: "cmd+k",
                  }}
                >
                  <FontAwesomeIcon
                    className={classNames({
                      "group-hover:tw-hidden": editorState.isLink,
                    })}
                    icon={faChain}
                  />
                  {editorState.isLink && (
                    <FontAwesomeIcon className='tw-hidden group-hover:tw-inline' icon={faChainBroken} />
                  )}
                </MenuBarIcon>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function MenuBarIcon({
  children,
  active = false,
  tooltip,
  hotkey,
  disabled = false,
  onClick = () => {},
}: PropsWithChildren<{
  active?: boolean;
  tooltip: string | React.ReactNode;
  hotkey?: { win: string; mac: string };
  disabled?: boolean;
  onClick?: () => void;
}>) {
  return (
    <Tooltip
      content={
        <div className='tw-flex tw-items-baseline tw-gap-2'>
          <span className='tw-font-bold'>{tooltip}</span>
          {hotkey && <Hotkey win={hotkey.win} mac={hotkey.mac} />}
        </div>
      }
    >
      <Button
        type='button'
        active={active}
        disabled={disabled}
        onClick={onClick}
        className={classNames(
          "menu-bar-button tw-group tw-m-0 tw-h-7 tw-w-7 tw-p-0 tw-transition-all tw-duration-100 focus-visible:tw-ring-2 focus-visible:tw-ring-primary",
          {
            "tw-bg-transparent": !active,
            "active tw-bg-primary tw-text-white": active,
          },
        )}
      >
        {children}
      </Button>
    </Tooltip>
  );
}

function Hotkey({ win, mac }: { win: string; mac: string }) {
  const { isMobile, isMac } = useUserAgent();
  const forceWindows = false;

  if (isMobile) return null;

  const macStringToCharacters: Record<string, string> = {
    cmd: "⌘",
    shift: "⇧",
    opt: "⌥",
    ctrl: "⌃",
  };

  const parts =
    isMac && !forceWindows
      ? mac
          .split("+")
          .map((part) => macStringToCharacters[part.trim().toLowerCase()]?.trim() || part?.trim())
          .map((part) => part.toUpperCase())
      : win
          .split("+")
          .map((part) => part?.trim())
          .map((part) => part.toUpperCase());

  return (
    <div className='tw-z-50 tw-flex tw-items-center tw-gap-1'>
      {parts.map((part, index) => (
        <div
          key={index}
          className='tw-border-1 tw-flex tw-h-6 tw-min-w-6 tw-items-center tw-justify-center tw-rounded tw-border tw-border-solid tw-border-grey-30 tw-px-1 tw-text-sm tw-font-black tw-shadow-[inset_0px_-3px_0px_0px_rgba(255,255,255,0.1)]'
        >
          <span className='-tw-mt-1'>{part}</span>
        </div>
      ))}
    </div>
  );
}

function HeaderDropdown({ hasWhiteMenuBar }: { hasWhiteMenuBar?: boolean }) {
  const { editor } = useCurrentEditor();
  if (!editor) return null;

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isParagraph: ctx.editor.isActive("paragraph") ?? false,
        isHeading1: ctx.editor.isActive("heading", { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive("heading", { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive("heading", { level: 3 }) ?? false,
        isHeading4: ctx.editor.isActive("heading", { level: 4 }) ?? false,
        isHeading5: ctx.editor.isActive("heading", { level: 5 }) ?? false,
        isHeading6: ctx.editor.isActive("heading", { level: 6 }) ?? false,
        currentHeadingLevel: ctx.editor.getAttributes("heading").level ?? null,
      };
    },
  });

  const items = [
    {
      key: "paragraph",
      text: "Paragraph",
      icon: faParagraph,
      hotkey: { win: "ctrl+alt+0", mac: "cmd+opt+0" },
      onClick: () => editor.chain().focus().setParagraph().run(),
    },
    {
      key: "heading1",
      text: "Heading 1",
      icon: faHeading,
      hotkey: { win: "ctrl+alt+1", mac: "cmd+opt+1" },
      onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
    },
    {
      key: "heading2",
      text: "Heading 2",
      icon: faHeading,
      hotkey: { win: "ctrl+alt+2", mac: "cmd+opt+2" },
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
    },
    {
      key: "heading3",
      text: "Heading 3",
      icon: faHeading,
      hotkey: { win: "ctrl+alt+3", mac: "cmd+opt+3" },
      onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
    },
    {
      key: "heading4",
      text: "Heading 4",
      icon: faHeading,
      hotkey: { win: "ctrl+alt+4", mac: "cmd+opt+4" },
      onClick: () => editor.chain().focus().toggleHeading({ level: 4 }).run(),
    },
    {
      key: "heading5",
      text: "Heading 5",
      icon: faHeading,
      hotkey: { win: "ctrl+alt+5", mac: "cmd+opt+5" },
      onClick: () => editor.chain().focus().toggleHeading({ level: 5 }).run(),
    },
    {
      key: "heading6",
      text: "Heading 6",
      icon: faHeading,
      hotkey: { win: "ctrl+alt+6", mac: "cmd+opt+6" },
      onClick: () => editor.chain().focus().toggleHeading({ level: 6 }).run(),
    },
  ];

  const selectedKey = () => {
    if (editorState.currentHeadingLevel !== null) return `heading${editorState.currentHeadingLevel}`;
    return "paragraph";
  };

  const shouldDisable = editorState.isParagraph === false && editorState.currentHeadingLevel === null;

  return (
    <Select
      aria-label={editorState.isParagraph ? "Paragraph" : `Heading ${editorState.currentHeadingLevel}`}
      selectedKey={selectedKey()}
      onSelectionChange={(key) => {
        const item = items.find((i) => i.key === key);
        item?.onClick();
      }}
      defaultSelectedKey={"paragraph"}
      isDisabled={shouldDisable}
    >
      <AriaButton
        className={classNames(
          "tw-flex tw-h-9 tw-min-w-48 tw-cursor-pointer tw-items-center tw-justify-between tw-gap-3 tw-overflow-hidden tw-rounded tw-px-3 tw-font-lato tw-outline-none tw-transition-all tw-duration-100 hover:tw-bg-white-dark focus-visible:tw-ring-2 focus-visible:tw-ring-primary",
          {
            "tw-border tw-border-solid tw-border-white-background tw-bg-white-background": !hasWhiteMenuBar,
            "tw-border tw-border-solid tw-border-border tw-bg-white": hasWhiteMenuBar,
          },
        )}
      >
        <SelectValue className='tw-flex tw-items-center tw-gap-2' />
        <FontAwesomeIcon aria-hidden='true' className='tw-h-3 tw-w-3 tw-text-text-hint' icon={faChevronDown} />
      </AriaButton>
      <Popover className='-tw-mt-1 tw-min-w-48 tw-rounded tw-bg-white tw-p-3 tw-shadow-md tw-ring-1 tw-ring-border'>
        <ListBox className='tw-space-y-1'>
          {items.map(({ key, text, icon, hotkey }) => (
            <ListBoxItem
              key={key}
              id={key}
              className='tw-flex tw-cursor-pointer tw-gap-3 tw-rounded tw-outline-none tw-transition-all tw-duration-100 hover:tw-bg-white-background focus-visible:tw-ring-2 focus-visible:tw-ring-primary'
            >
              <Tooltip content={<Hotkey {...hotkey} />} offset={[0, 4]} position='right center'>
                <div className='tw-flex tw-h-full tw-w-full tw-items-center tw-gap-3 tw-p-[8px]'>
                  <FontAwesomeIcon
                    className={classNames({
                      "tw-text-primary-dark": selectedKey() === key,
                    })}
                    icon={icon}
                  />
                  <span
                    className={classNames("tw-font-bold", {
                      "tw-text-text-secondary": selectedKey() !== key,
                      "tw-text-text": selectedKey() === key,
                    })}
                  >
                    {text}
                  </span>
                </div>
              </Tooltip>
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );
}

// Allows user to immediately open tooltips as they swipe their pointer across buttons
const useContinuousTooltip = create<{
  skipDelay: boolean;
  timeoutHandle: number | null;
  tooltipProps: (props: { enterDelay?: number; leaveDelay?: number }) => Record<string, unknown>;
  onTooltipOpen: () => void;
}>()((set, get) => ({
  skipDelay: false,
  timeoutHandle: null,
  tooltipProps: ({ enterDelay, leaveDelay }: { enterDelay?: number; leaveDelay?: number }) => {
    const { skipDelay, onTooltipOpen } = get();
    const actualEnterDelay = enterDelay ?? 400;
    const actualLeaveDelay = leaveDelay ?? 400;
    return {
      onOpen: onTooltipOpen,
      mouseEnterDelay: skipDelay ? 0 : actualEnterDelay,
      mouseLeaveDelay: skipDelay ? 0 : actualLeaveDelay,
    };
  },
  onTooltipOpen: () => {
    const { timeoutHandle } = get();
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
    set({
      skipDelay: true,
      timeoutHandle: setTimeout(() => {
        set({ skipDelay: false, timeoutHandle: null });
      }, 750),
    });
  },
}));

function Tooltip({
  children,
  content,
  offset,
  position = "top center",
}: PropsWithChildren<{
  content: string | React.ReactNode;
  offset?: [number, number];
  position?: "top center" | "bottom center" | "left center" | "right center";
}>) {
  const { tooltipProps } = useContinuousTooltip();

  return (
    <Popup
      {...tooltipProps({ enterDelay: 400, leaveDelay: 100 })}
      on='hover'
      position={position}
      className='tw-z-[9999] tw-px-2 tw-py-1 tw-font-bold'
      inverted
      offset={offset}
      trigger={children}
      hideOnScroll
    >
      {content}
    </Popup>
  );
}
